import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Card, Close as CloseIcon, IconButton, makeStyles, Typography } from 'housecall-ui';
import Button from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/Button';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';

import ImagePlaceholder from '../../src/components/photos/ImagePlaceholder';
import { Spinner } from '../../src/components/Spinner';
import { Photo } from '../../src/state/types';
import { useUploadPhoto } from '../hooks/useUploadPhoto';

type Props = {
  photo: Photo;
};

type StyleProps = {
  isError: boolean;
};

const useStyles = makeStyles((theme: HCPTheme) => ({
  fileName: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  card: {
    height: theme.spacing(10),
    maxHeight: theme.spacing(10),
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    border: ({ isError }: StyleProps) => (isError ? `1px solid ${theme.palette.error.main}` : 'none'),
  },
  nameBox: {
    minWidth: 0,
  },
  image: {
    minWidth: theme.spacing(15),
    maxWidth: theme.spacing(15),
    height: theme.spacing(10),
  },
  placeholder: {
    height: theme.spacing(10),
    width: theme.spacing(15),
  },
}));

const PhotoPreview = ({ photo }: Props) => {
  const showError = Boolean(photo.isError && !photo.isLoading);
  const { fileName, card, nameBox, image, placeholder } = useStyles({ isError: showError });
  const isHeic = photo.file.type.includes('heic');

  const { token } = useParams<{ token: string }>();
  const { uploadPhoto, deletePhoto } = useUploadPhoto({ token });
  const [isRetried, setRetried] = useState(false);

  const onDelete = () => {
    deletePhoto(photo.file);
  };

  const onRetry = () => {
    setRetried(true);
    uploadPhoto(photo.file);
  };

  return (
    <Card className={card}>
      <Box display="flex" alignItems="center">
        <Box display="flex" alignItems="center" minWidth={0} flex="0 1 auto" mr="auto">
          {isHeic ? (
            <ImagePlaceholder className={placeholder} data-testid="service-photo-sample-preview-img" />
          ) : (
            <img
              src={URL.createObjectURL(photo.file)}
              alt={`Preview ${photo.file.name}`}
              className={image}
              data-testid="service-photo-preview-img"
            />
          )}
          <Box ml={2} className={nameBox}>
            <Typography className={fileName}>{photo.file.name}</Typography>
            {showError ? (
              <Typography variant="body2" color="error">
                Failed to upload image
              </Typography>
            ) : null}
          </Box>
        </Box>
        <Box display="flex" flexWrap="no-wrap">
          {showError && !isRetried ? (
            <Button color="primary" size="small" onClick={onRetry} data-testid="service-photo-retry-btn">
              Retry
            </Button>
          ) : null}
          <IconButton onClick={onDelete} data-testid="service-photo-remove-btn">
            <CloseIcon />
          </IconButton>
        </Box>

        {photo.isLoading ? (
          <Box ml={2} mt={2}>
            <Spinner show size={32} />
          </Box>
        ) : null}
      </Box>
    </Card>
  );
};

export default PhotoPreview;
